// Composite weld-bay picker rendered above the stage details table.
//
// Single mode (spools — `chosen`): click a bay to swap the spool's
// welding to it; the parent migrates the active bay's tracking onto
// the new bay (planBaySwap) and the first non-skipped weld row in
// the catalogue becomes the inferred bay on the next refetch — see
// inferWeldBayKey.
//
// Multi mode (Simple-view job pipeline — `activeKeys`): a job can
// weld in several bays at once, so each click toggles that bay
// on/off instead of swapping. The "—" button clears every bay.
import { useMemo } from "react";
import {
  STAGES,
  type StageDefDto,
  type StageKey,
} from "../../../../../../shared/types/production-schedule";

interface Props {
  chosen: StageKey | null;
  // When provided the picker runs in multi-select mode: these keys
  // highlight as active and `chosen` is ignored for highlighting.
  activeKeys?: ReadonlySet<string>;
  catalogue?: StageDefDto[];
  onPick(next: StageKey | null): void | Promise<void>;
  busy?: boolean;
}

export function BayPicker({ chosen, activeKeys, catalogue, onPick, busy }: Props) {
  const multi = activeKeys !== undefined;
  const weldDefs = useMemo(() => {
    if (catalogue) return catalogue.filter((d) => d.weld === true && !d.archived);
    return STAGES.filter((s) => s.weld === true).map((s, i) => ({
      stageKey: s.key,
      short: s.short,
      name: s.name,
      color: s.color,
      hasHours: s.hasHours,
      weld: true,
      bay: s.bay ?? null,
      sortOrder: i,
      archived: false,
      isCustom: false,
      updatedAt: "",
    } as StageDefDto));
  }, [catalogue]);

  const noneActive = multi ? activeKeys.size === 0 : chosen === null;

  return (
    <div className="bay-picker">
      <span className="bay-picker-label">{multi ? "Bays" : "Bay"}</span>
      {weldDefs.map((def) => {
        const active = multi ? activeKeys.has(def.stageKey) : chosen === def.stageKey;
        return (
          <button
            key={def.stageKey}
            type="button"
            disabled={busy}
            title={multi
              ? `${active ? "Remove" : "Add"} ${def.name}`
              : `Switch welding to ${def.name}`}
            className={`bay-picker-btn${active ? " active" : ""}`}
            style={{
              borderColor: active ? def.color : undefined,
              background: active ? def.color : "transparent",
              color: active ? "#fff" : def.color,
            }}
            onClick={() => void onPick(def.stageKey as StageKey)}
          >
            {def.short}
          </button>
        );
      })}
      <button
        type="button"
        disabled={busy || noneActive}
        title={multi
          ? "Clear all welding bays (no welding on this job)"
          : "Clear welding bay (no welding for this spool)"}
        className={`bay-picker-btn${noneActive ? " active none" : " none"}`}
        onClick={() => void onPick(null)}
      >
        —
      </button>
    </div>
  );
}
